/**
 * Identidade do ambiente da planilha GERENCIAL para o histórico de comentários.
 *
 * O ambiente é "PEDIDO:AMBIENTE" normalizado (`chaveAmbiente`, a mesma chave
 * do Mapa da Produção). Mudar de etapa não muda a chave — as 16 páginas do
 * Legado são filtros da mesma aba —, então o histórico acompanha o ambiente
 * por todas elas. A chave só é confiável quando aponta UMA linha de UM
 * cliente; nos casos abaixo a tela bloqueia o comentário em vez de adivinhar
 * (decisão do Igor, 01/10/2026):
 *
 * - linha sem pedido ou sem ambiente (sem chave);
 * - o mesmo pedido para clientes diferentes (a chave colidiria entre eles);
 * - dois ambientes com o mesmo nome no mesmo pedido.
 */
import { chaveAmbiente, rotuloAmbiente } from "../pages/MapaProducao/dados/chaves";
import { hasValue, normalizeText } from "./data";
import { F } from "./gerencialFields";

function texto(valor) {
  return hasValue(valor) ? String(valor).replace(/\s+/g, " ").trim() : "";
}

/**
 * Varre a planilha uma vez: `{ linhasPorChave, pedidosComVariosClientes,
 * ambientesRepetidos }`. As listas servem ao alerta da página Início; o mapa
 * serve ao bloqueio de cada painel.
 */
export function indiceDeIdentidade(linhas = []) {
  const linhasPorChave = new Map();
  const clientesPorPedido = new Map();
  (Array.isArray(linhas) ? linhas : []).forEach((row) => {
    const pedido = normalizeText(texto(row?.[F.NUMERO_DO_PEDIDO]));
    const cliente = texto(row?.[F.NOME_DO_CLIENTE]);
    if (pedido && cliente) {
      const porNome = clientesPorPedido.get(pedido) || { pedido: texto(row[F.NUMERO_DO_PEDIDO]), clientes: new Map() };
      const chaveCliente = normalizeText(cliente);
      if (!porNome.clientes.has(chaveCliente)) porNome.clientes.set(chaveCliente, cliente);
      clientesPorPedido.set(pedido, porNome);
    }
    const chave = chaveAmbiente(row);
    if (!chave) return;
    const atual = linhasPorChave.get(chave) || [];
    atual.push(row);
    linhasPorChave.set(chave, atual);
  });

  const pedidosComVariosClientes = [...clientesPorPedido.entries()]
    .filter(([, item]) => item.clientes.size > 1)
    .map(([pedidoNormalizado, item]) => ({
      pedidoNormalizado,
      pedido: item.pedido,
      clientes: [...item.clientes.values()].sort((a, b) => a.localeCompare(b, "pt-BR")),
    }))
    .sort((a, b) => a.pedido.localeCompare(b.pedido, "pt-BR", { numeric: true }));
  const pedidosAmbiguos = new Set(pedidosComVariosClientes.map((p) => p.pedidoNormalizado));

  const ambientesRepetidos = [...linhasPorChave.entries()]
    .filter(([, rows]) => rows.length > 1)
    .map(([chave, rows]) => ({
      chave,
      rotulo: rotuloAmbiente(rows[0]),
      cliente: texto(rows[0][F.NOME_DO_CLIENTE]),
      linhas: rows.length,
    }))
    // O pedido com dois clientes já aparece na outra lista; repetir aqui
    // contaria o mesmo problema duas vezes.
    .filter((item) => !pedidosAmbiguos.has(item.chave.split(":")[0]))
    .sort((a, b) => a.chave.localeCompare(b.chave, "pt-BR", { numeric: true }));

  return { linhasPorChave, pedidosAmbiguos, pedidosComVariosClientes, ambientesRepetidos };
}

/**
 * `null` quando o ambiente pode ser comentado; senão `{ motivo, mensagem }`.
 * Sem índice (planilha ainda carregando) só o caso sem chave é decidido.
 */
export function bloqueioDoComentario(row, indice) {
  const chave = chaveAmbiente(row);
  if (!chave) {
    return {
      motivo: "sem_chave",
      mensagem: "Preencha o NUM. PEDIDO e o AMBIENTE desta linha na planilha para habilitar os comentários.",
    };
  }
  if (!indice) return null;
  const pedido = chave.split(":")[0];
  if (indice.pedidosAmbiguos.has(pedido)) {
    const item = indice.pedidosComVariosClientes.find((p) => p.pedidoNormalizado === pedido);
    return {
      motivo: "pedido_com_varios_clientes",
      mensagem:
        `O pedido ${item?.pedido || pedido} aparece na planilha para mais de um cliente ` +
        `(${(item?.clientes || []).join(", ")}). Corrija o número do pedido na planilha para comentar — ` +
        "sem isso o comentário poderia cair no ambiente do outro cliente.",
    };
  }
  if ((indice.linhasPorChave.get(chave) || []).length > 1) {
    return {
      motivo: "ambiente_repetido",
      mensagem:
        `Há mais de um ambiente "${texto(row[F.AMBIENTE])}" no pedido ${texto(row[F.NUMERO_DO_PEDIDO])}. ` +
        "Diferencie os nomes na planilha (ex.: DORMITÓRIO CASAL e DORMITÓRIO FILHO) para comentar.",
    };
  }
  return null;
}
